import { Navigate } from "src/router";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { lazyNamed, lazyWithRetry } from "src/utils/lazy-named";
import { audienceDetailLoader, audiencesListLoader } from "./loader";
const CreateAudienceTrait = lazyNamed(
  () =>
    import("src/components/audiences/audience-traits/create-audience-trait"),
  "CreateAudienceTrait",
);
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { FormErrorProvider } from "src/contexts/form-error-context";
import { PollingContextProvider } from "src/pages/dashboards/polling/provider";

const AudienceDetails = lazyNamed(
  () => import("./audience"),
  "AudienceDetails",
);
const AudienceLoader = lazyNamed(() => import("./audience"), "AudienceLoader");
const AudienceActivity = lazyNamed(
  () => import("./audience/activity"),
  "AudienceActivity",
);
const AudienceDefinition = lazyNamed(
  () => import("./audience/definition"),
  "AudienceDefinition",
);
const AudienceSettings = lazyNamed(
  () => import("./audience/settings"),
  "AudienceSettings",
);
const AudienceSplits = lazyNamed(
  () => import("./audience/splits"),
  "AudienceSplits",
);
const AudienceSyncs = lazyNamed(
  () => import("./audience/syncs"),
  "AudienceSyncs",
);
const AudienceMembers = lazyNamed(
  () => import("./audience/members"),
  "AudienceMembers",
);
const AudienceTraits = lazyNamed(
  () => import("./audience/traits"),
  "AudienceTraits",
);
const AudienceUsage = lazyNamed(
  () => import("./audience/usage"),
  "AudienceUsage",
);
const audiencesImport = () => import("./audiences");
const AudiencesLoader = lazyWithRetry(audiencesImport);

export const preloads: RoutePreload[] = [["/audiences", audiencesImport]];
const CloneAudience = lazyNamed(
  () => import("./clone-audience"),
  "CloneAudience",
);
const CreateAudience = lazyNamed(
  () => import("./create-audience"),
  "CreateAudience",
);
const AdminVqfEditor = lazyNamed(
  () => import("./audience/admin-vqf-editor"),
  "AdminVqfEditor",
);
const AudienceOverview = lazyNamed(
  () => import("src/pages/audiences/audience/overview"),
  "AudienceOverview",
);
const AudienceDetailsIndexRedirect = lazyNamed(
  () => import("src/pages/audiences/details-index-redirect"),
  "AudienceDetailsIndexRedirect",
);

export const routes: Array<RouteObject> = [
  {
    path: "audiences",
    spacesAccess: "accessible-from-limited-view",
    loader: audiencesListLoader,
    element: <AudiencesLoader />,
    children: [],
  },
  {
    path: "audiences/:id",
    spacesAccess: "accessible-from-limited-view",
    loader: audienceDetailLoader,
    element: <AudienceLoader />,
    children: [
      {
        path: "*",
        spacesAccess: "accessible-from-limited-view",
        element: <AudienceDetails />,
        children: [
          {
            index: true,
            spacesAccess: "accessible-from-limited-view",
            element: <AudienceDetailsIndexRedirect />,
          },
          {
            path: "overview",
            spacesAccess: "accessible-from-limited-view",
            element: (
              <PollingContextProvider>
                <AudienceOverview />
              </PollingContextProvider>
            ),
          },
          {
            path: "definition",
            spacesAccess: "accessible-from-limited-view",
            element: <AudienceDefinition />,
          },
          {
            path: "splits",
            spacesAccess: "accessible-from-limited-view",
            element: <AudienceSplits />,
          },
          {
            path: "members",
            spacesAccess: "accessible-from-limited-view",
            element: <AudienceMembers />,
          },
          {
            path: "syncs",
            spacesAccess: "accessible-from-limited-view",
            element: <AudienceSyncs />,
          },
          {
            path: "traits",
            spacesAccess: "accessible-from-limited-view",
            element: <AudienceTraits />,
            children: [
              {
                path: "new",
                spacesAccess: "accessible-from-limited-view",
                element: (
                  <FormErrorProvider>
                    <CreateAudienceTrait />
                  </FormErrorProvider>
                ),
              },
            ],
          },
          {
            path: "activity",
            spacesAccess: "accessible-from-limited-view",
            element: <AudienceActivity />,
          },
          {
            path: "usage",
            // Lineage isn't supported in spaces yet.
            spacesAccess: "hidden-from-limited-view",
            element: (
              <FeatureFlagRoute flagKey="lineageGraphUsage">
                <AudienceUsage />
              </FeatureFlagRoute>
            ),
          },
          {
            path: "settings",
            spacesAccess: "accessible-from-limited-view",
            element: <AudienceSettings />,
          },
          {
            path: "query",
            spacesAccess: "accessible-from-limited-view",
            element: <Navigate to="../definition" replace />,
          },
        ],
      },
    ],
  },
  {
    path: "audiences/new",
    spacesAccess: "accessible-from-limited-view",
    element: (
      <FormErrorProvider>
        <CreateAudience />
      </FormErrorProvider>
    ),
  },
  {
    path: "audiences/:id/clone",
    spacesAccess: "accessible-from-limited-view",
    element: (
      <FormErrorProvider>
        <CloneAudience />
      </FormErrorProvider>
    ),
  },
  {
    path: "audiences/admin-vqf-editor",
    spacesAccess: "accessible-from-limited-view",
    element: (
      <PermissionedRoute
        requireImpersonationPrivileges
        permission={{
          v2: { resource: "workspace", grant: "can_update" },
        }}
        redirect="/audiences"
      >
        <AdminVqfEditor />
      </PermissionedRoute>
    ),
  },
];
